import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import MoneyInput from '../components/MoneyInput';
import ClienteSearch from '../components/ClienteSearch';
import VinculacionModal from '../components/VinculacionModal';

const hoyStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const emptyForm = () => ({ deudor_nombre: '', cliente_id: '', concepto: '', monto_total: '', fecha_generacion: hoyStr(), fecha_vencimiento: '', estado: 'vigente', expediente_id: '', tramite_id: '' });

export default function DeudaModal({ open, onClose, editing = null, onSaved }) {
  const [clientes, setClientes] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  const [tramites, setTramites] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [vincularOpen, setVincularOpen] = useState(false);

  const editingId = editing?.id || null;
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const fetchClientes = useCallback(async () => {
    const { data } = await supabase.from('clientes').select('id, nombre, apellido').eq('eliminado', false).order('nombre');
    setClientes(data || []);
  }, []);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [, exp, tra] = await Promise.all([
        fetchClientes(),
        supabase.from('expedientes').select('id, numero, caratula').eq('eliminado', false).order('caratula'),
        supabase.from('tramites').select('id, nombre').eq('eliminado', false).order('nombre'),
      ]);
      setExpedientes(exp.data || []);
      setTramites(tra.data || []);
    })();
    if (editing) {
      setForm({
        deudor_nombre: editing.deudor_nombre || '', cliente_id: editing.cliente_id || '',
        concepto: editing.concepto || '', monto_total: editing.monto_total || '',
        fecha_generacion: editing.fecha_generacion || '', fecha_vencimiento: editing.fecha_vencimiento || '',
        estado: editing.estado || 'vigente', expediente_id: editing.expediente_id || '',
        tramite_id: editing.tramite_id || '',
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, editing, fetchClientes]);

  // Etiqueta del expediente/trámite vinculado
  let vinculo = null;
  if (form.expediente_id) {
    const e = expedientes.find(x => x.id === form.expediente_id);
    vinculo = { icon: '📁', tipo: 'Expediente', label: e ? `${e.numero ? e.numero + ' — ' : ''}${e.caratula || ''}` : 'Expediente' };
  } else if (form.tramite_id) {
    const t = tramites.find(x => x.id === form.tramite_id);
    vinculo = { icon: '📋', tipo: 'Trámite', label: t ? t.nombre : 'Trámite' };
  }

  async function saveDeuda() {
    if (!form.monto_total || parseFloat(form.monto_total) <= 0) { showToast('Ingresá un monto válido'); return; }
    if (!form.concepto.trim()) { showToast('El concepto es obligatorio'); return; }
    setSaving(true);
    const deudaData = {
      deudor_nombre: form.cliente_id ? null : form.deudor_nombre,
      cliente_id: form.cliente_id || null, concepto: form.concepto,
      monto_total: parseFloat(form.monto_total),
      fecha_generacion: form.fecha_generacion || null,
      fecha_vencimiento: form.fecha_vencimiento || null,
      estado: form.estado, expediente_id: form.expediente_id || null,
      tramite_id: form.tramite_id || null,
    };
    let error;
    if (editingId) {
      ({ error } = await supabase.from('deudas').update(deudaData).eq('id', editingId));
    } else {
      const result = await supabase.from('deudas').insert(deudaData).select('id').single();
      error = result.error;
    }
    setSaving(false);
    if (error) { showToast('Error al guardar'); return; }
    showToast(editingId ? 'Deuda actualizada ✓' : 'Deuda registrada ✓');
    onSaved?.();
    onClose();
  }

  async function eliminarDeuda() {
    if (!window.confirm('¿Eliminar esta deuda?')) return;
    const { error } = await supabase.rpc('eliminar_deuda', { p_deuda_id: editingId });
    if (error) { showToast('Error al eliminar'); return; }
    onSaved?.();
    onClose();
  }

  if (!open) return null;

  return (
    <>
      <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
        <div className="modal" style={{ width: 500 }}>
          <div className="modal-header">
            <div className="modal-title">{editingId ? 'Editar deuda' : 'Nueva deuda'}</div>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group form-full">
                <label className="form-label">Cliente</label>
                <ClienteSearch
                  clientes={clientes}
                  value={form.cliente_id}
                  onChange={id => setForm(f => ({ ...f, cliente_id: id, deudor_nombre: '' }))}
                  allowManual
                  allowCreate
                  onClienteCreado={fetchClientes}
                  manualValue={form.deudor_nombre}
                  onManualChange={v => setForm(f => ({ ...f, deudor_nombre: v }))}
                  placeholder="Buscar cliente por nombre o DNI..."
                />
              </div>
              <div className="form-group form-full">
                <label className="form-label">Concepto *</label>
                <input className="form-input" value={form.concepto} onChange={e => setForm(f => ({ ...f, concepto: e.target.value }))} placeholder="Ej: Honorarios juicio laboral" />
              </div>
              <div className="form-group">
                <label className="form-label">Monto total ($) *</label>
                <MoneyInput value={form.monto_total} onChange={v => setForm(f => ({ ...f, monto_total: v }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Estado</label>
                <select className="form-select" value={form.estado} onChange={e => setForm(f => ({ ...f, estado: e.target.value }))}>
                  <option value="vigente">Vigente</option>
                  <option value="en gestión">En gestión</option>
                  <option value="cobrada">Cobrada</option>
                  <option value="incobrable">Incobrable</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Fecha de generación</label>
                <input className="form-input" type="date" value={form.fecha_generacion} onChange={e => setForm(f => ({ ...f, fecha_generacion: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Fecha de vencimiento</label>
                <input className="form-input" type="date" value={form.fecha_vencimiento} onChange={e => setForm(f => ({ ...f, fecha_vencimiento: e.target.value }))} />
              </div>
              <div className="form-group form-full">
                <label className="form-label">Expediente / Trámite vinculado</label>
                {vinculo ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 8 }}>
                    <span style={{ fontSize: 16, flexShrink: 0 }}>{vinculo.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 10, color: 'var(--text4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{vinculo.tipo}</div>
                      <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vinculo.label}</div>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => setVincularOpen(true)} style={{ flexShrink: 0 }}>Cambiar</button>
                  </div>
                ) : (
                  <button className="btn btn-ghost" onClick={() => setVincularOpen(true)} style={{ width: '100%', justifyContent: 'center' }}>
                    + Vincular expediente o trámite
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            {editingId && (
              <button className="btn btn-danger btn-sm" onClick={eliminarDeuda} style={{ marginRight: 'auto' }}>Eliminar</button>
            )}
            <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={saveDeuda} disabled={saving}>{saving ? 'Guardando…' : 'Guardar deuda'}</button>
          </div>
        </div>
      </div>

      <VinculacionModal
        open={vincularOpen}
        onClose={() => setVincularOpen(false)}
        tabs={['expedientes', 'tramites']}
        value={{ expediente_id: form.expediente_id, tramite_id: form.tramite_id }}
        onSelect={(v) => setForm(f => ({ ...f, expediente_id: v.expediente_id, tramite_id: v.tramite_id }))}
      />

      {toast && <div className="toast" style={{ zIndex: 1200 }}>✓ {toast}</div>}
    </>
  );
}
